import React from "react";
import { Link } from "react-router-dom";
import { StatusBadge } from "./StatusBadge";
import { ProductImage } from "./ProductImage";
import { formatPrice } from "../../lib/format";
import { ProductPurchaseActions, isPurchasable } from "./ProductPurchaseActions";
import { FormBadge, formulationLine } from "./FormBadge";

/**
 * Product card — ecommerce-led hierarchy (Feb 2026 redesign):
 *   1. image
 *   2. product name
 *   3. size / formulation
 *   4. price
 *   5. Add-to-Cart CTA
 * SKU + storage + RUO stay present but visually quieter.
 *
 * Low-stock cue (Feb 2026):
 *   * When the product is purchasable AND `inventory_quantity` is a
 *     positive integer <= the row's `low_stock_threshold` (defaults 5),
 *     we render an honest "Only N left" chip in place of the generic
 *     "In Stock" pill.  Numbers come straight from the live catalog —
 *     no invented urgency, no countdown timers, no fake stock counts.
 *   * Otherwise the plain "In Stock" pill is shown, matching prior UX.
 */
export const ProductCard = ({ product }) => {
  const purchasable = isPurchasable(product);
  const showPrice =
    purchasable &&
    product.public_price_visible !== false &&
    !!product.retail_price_cents;

  // Honest low-stock signal — only fires when both fields are present and
  // the row is actually purchasable.  Never invented.
  const invQty = typeof product.inventory_quantity === "number" ? product.inventory_quantity : null;
  const lowThreshold = typeof product.low_stock_threshold === "number" && product.low_stock_threshold > 0
    ? product.low_stock_threshold
    : 5;
  const showLowStock = purchasable && invQty !== null && invQty > 0 && invQty <= lowThreshold;

  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-[22px] bg-white border border-brand-border shadow-[0_2px_10px_rgba(10,35,66,0.05)] hover:shadow-[0_20px_44px_rgba(10,35,66,0.12)] hover:border-brand-accent/30 transition-[transform,box-shadow,border-color] duration-200"
    >
      <Link to={`/product/${product.slug}`} className="block relative">
        <div className="bg-gradient-to-br from-brand-surface to-white aspect-square overflow-hidden">
          <div className="w-full h-full transition-transform duration-500 group-hover:scale-[1.04]">
            <ProductImage
              product={product}
              size="thumbnail"
              padding="p-8"
              testId={`product-image-${product.slug}`}
            />
          </div>
        </div>
        {purchasable && (
          showLowStock ? (
            <span
              data-testid={`product-low-stock-${product.slug}`}
              className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-800 bg-amber-50 border border-amber-200 rounded-full"
            >
              Only {invQty} left
            </span>
          ) : (
            <span
              data-testid={`product-in-stock-${product.slug}`}
              className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full"
            >
              In Stock
            </span>
          )
        )}
        <span className="absolute top-3 right-3">
          <FormBadge format={product.format} />
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-6">
        {/* 1. Name — the loudest label on the card */}
        <Link to={`/product/${product.slug}`} className="block">
          <h3 className="font-display text-[18px] md:text-[19px] font-semibold text-brand-navy leading-snug tracking-tight line-clamp-2 min-h-[3rem] group-hover:text-brand-accent transition-colors duration-200">
            {product.product_name}
          </h3>
        </Link>

        {/* 2. Size / formulation */}
        <div
          className="mt-1.5 text-[13px] text-brand-muted font-medium min-h-[1.25rem]"
          data-testid={`form-line-${product.slug}`}
        >
          {formulationLine(product)}
        </div>

        {/* Made-in-USA microtag — one compact line, only on purchasable
            SKUs, and only on cards where price will be shown next.  A
            single subtle text row is enough at card scale; a fourth
            badge cluster would crowd the mobile grid. */}
        {purchasable && (
          <div
            data-testid={`product-usa-tag-${product.slug}`}
            className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-navy/85"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" aria-hidden />
            Made in the USA
          </div>
        )}

        {/* 3. Price */}
        <div className="mt-5">
          {showPrice ? (
            <div
              className="font-display text-[26px] font-bold text-brand-navy leading-none"
              data-testid={`price-${product.slug}`}
            >
              {formatPrice(product.retail_price_cents)}
            </div>
          ) : (
            <div
              className="text-[11px] font-semibold uppercase text-brand-muted tracking-[0.14em]"
              data-testid={`price-hidden-${product.slug}`}
            >
              Pricing on approval
            </div>
          )}
        </div>

        {/* 4. Add to Cart CTA */}
        <div className="mt-4">
          <ProductPurchaseActions product={product} variant="card" />
        </div>

        {/* 5. Secondary meta — SKU + RUO chip + compliance badge if not purchasable */}
        <div className="mt-5 pt-4 border-t border-brand-border flex flex-wrap items-center justify-between gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-muted">
            {product.public_sku}
          </div>
          {purchasable ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-amber-800 bg-amber-50 border border-amber-200 rounded-full">
              Research Use
            </span>
          ) : (
            <StatusBadge status={product.compliance_status} kind="compliance" />
          )}
        </div>
      </div>
    </article>
  );
};
